<script lang="ts">
  import { fetchPlace } from '../api/client'
  import { fmtCoords, placeLabel } from '../format'
  import { location } from '../state/location.svelte'
  import { askPersist, focusPlace, savePlace } from '../state/places.svelte'
  import { shownPlace } from '../state/shown'
  import Icon from './Icon.svelte'
  import { haptic } from '../native'

  /**
   * The star in the panel bar, on both layouts (Phase 11): a square ink
   * button, outlined unsaved and filled saved. Empty saves
   * the point on screen; filled opens that place's row in Settings rather
   * than un-saving it, because a renamed place lost to a mis-tap cannot be
   * got back, and the row's Remove button is one more tap.
   */

  interface Props {
    size?: number
  }

  let { size = 20 }: Props = $props()

  let saved = $derived(shownPlace())
  let busy = $state(false)

  async function onclick() {
    const point = location.selected
    if (!point || busy) return
    if (saved) {
      focusPlace(saved.id)
      return
    }
    // Inside the gesture, before anything is awaited.
    void askPersist()
    const generation = location.generation
    busy = true
    try {
      // A search's label is the ZIP's town or the place's own name and is
      // used as it is. Anything else is named by `/api/place`, distance and
      // all ("8 mi from Lawrence, KS"). Offline, with no answer, the
      // coordinates are the only name that claims nothing, and the place can
      // be renamed in Settings.
      const nws =
        point.source === 'search' && point.label ? point.label : await nwsName(point.lat, point.lon)
      if (location.generation !== generation) return // Moved on while asking.
      const result = savePlace({
        lat: point.lat,
        lon: point.lon,
        name: nws || fmtCoords(point.lat, point.lon),
        label: nws || undefined,
        zip: point.zip,
      })
      if (result.kind === 'saved') haptic('success')
    } finally {
      busy = false
    }
  }

  async function nwsName(lat: number, lon: number): Promise<string> {
    try {
      return placeLabel(await fetchPlace(lat, lon))
    } catch {
      return ''
    }
  }
</script>

<button
  type="button"
  class="star touch-target"
  class:saved
  aria-label={saved ? `Saved as ${saved.name}. Edit in Settings` : 'Save this place'}
  title={saved ? `Saved as ${saved.name}` : 'Save this place'}
  disabled={busy}
  {onclick}
>
  <Icon name="star" {size} filled={!!saved} />
</button>

<style>
  /* 2 px of ink round the star, which is filled when the place is saved:
     the warn yellow is a second voice the poster has no room for. */
  .star {
    flex: 0 0 auto;
    display: inline-grid;
    place-items: center;
    width: 38px;
    height: 38px;
    padding: 0;
    background: none;
    border: 2px solid var(--color-text);
    border-radius: 0;
    color: var(--color-text);
    cursor: pointer;
  }

  @media (hover: hover) {
    .star:not(:disabled):hover {
      background: var(--color-surface);
    }
  }

  .star:disabled {
    opacity: 0.6;
    cursor: default;
  }
</style>
